import { FC, useContext } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import { BorderConfiguration, BorderConfigurationData } from "../../../http/border/types/configurations";
import { isBorderUprightReclinable } from "../../../http/border/types/uprightTypes";
import { UpdateConfigurationBody } from "../../../http/types/configurations";
import { useConfigurationsApi } from "../../../http/useConfigurations";
import BorderAggregateMeasurementsStep, {
  BorderAggregateMeasurementsFormBody,
} from "./BorderAggregateMeasurementsStep";
import BorderManualMeasurementsStep, {
  BorderManualMeasurementsFormBody,
  metersToMillimeters,
} from "./BorderManualMeasurementsStep";

type BorderPerimeterStepProps = {
  configuration: BorderConfiguration;
  borderData: BorderConfigurationData;
  configurationId: string;
  canEdit?: boolean;
  isAdmin?: boolean;
  disabled?: boolean;
  needsOrientation: boolean;
  isPerimeterSplitRequired: boolean;
  isCornerOrientationRequired: boolean;
  uprightTypeCode: string | null;
  next: () => void;
  back: () => void;
};

// Manual measurement entry is the only way to define a border's perimeter.
// This component picks which entry form to render -- the per-segment list
// for reclinable uprights, or the aggregate totals form for STRAIGHT/CURVED
// uprights -- and owns persisting either form's data back to the
// configuration.
const BorderPerimeterStep: FC<BorderPerimeterStepProps> = ({
  configuration,
  borderData,
  configurationId,
  canEdit,
  isAdmin,
  disabled,
  needsOrientation,
  isPerimeterSplitRequired,
  isCornerOrientationRequired,
  uprightTypeCode,
  next,
  back,
}) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const configurationsApi = useConfigurationsApi();

  const saveManualMeasurements = async (formBody: BorderManualMeasurementsFormBody) => {
    const border: BorderConfigurationData = {
      ...borderData,
      inputMethod: "manual",
      isClosedPerimeter: formBody.isClosedPerimeter,
      segments: formBody.segments.map((segment, index) => ({
        sequenceOrder: index,
        length: metersToMillimeters(segment.length),
        orientation: segment.orientation?.value ?? null,
        cornerClassification: segment.cornerClassification?.value ?? null,
      })),
      perimeterLength: null,
      perimeterLengthParallel: null,
      perimeterLengthPerpendicular: null,
      cornerCountTotal: null,
      cornerCountInternal: null,
      cornerCountExternal: null,
    };
    const body: UpdateConfigurationBody = {
      name: configuration.name,
      description: configuration.description ?? null,
      language: configuration.language,
      border,
    };
    const optimisticData: BorderConfiguration = { ...configuration, data: border };

    try {
      await configurationsApi.updateConfiguration(configuration.project?.id, configurationId, body, optimisticData);
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      dispatch(showSuccessNotification(i18n.translation));
    }
  };

  const saveAggregateMeasurements = async (formBody: BorderAggregateMeasurementsFormBody) => {
    // Aggregate mode is mutually exclusive with the per-segment fields: an
    // aggregate-mode save clears segments, and within the aggregate fields
    // themselves only the pair matching the current system/span combination
    // is kept -- the other pair is explicitly nulled out so no stale value
    // from a previous system/span combination lingers.
    const showCornerOrientationFields = isCornerOrientationRequired && borderData.span < 1500;
    const border: BorderConfigurationData = {
      ...borderData,
      inputMethod: "manual",
      segments: [],
      perimeterLength: isPerimeterSplitRequired ? null : metersToMillimeters(formBody.perimeterLength),
      perimeterLengthParallel: isPerimeterSplitRequired ? metersToMillimeters(formBody.perimeterLengthParallel) : null,
      perimeterLengthPerpendicular: isPerimeterSplitRequired
        ? metersToMillimeters(formBody.perimeterLengthPerpendicular)
        : null,
      cornerCountTotal: showCornerOrientationFields ? null : formBody.cornerCountTotal,
      cornerCountInternal: showCornerOrientationFields ? formBody.cornerCountInternal : null,
      cornerCountExternal: showCornerOrientationFields ? formBody.cornerCountExternal : null,
    };
    const body: UpdateConfigurationBody = {
      name: configuration.name,
      description: configuration.description ?? null,
      language: configuration.language,
      border,
    };
    const optimisticData: BorderConfiguration = { ...configuration, data: border };

    try {
      await configurationsApi.updateConfiguration(configuration.project?.id, configurationId, body, optimisticData);
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return isBorderUprightReclinable(uprightTypeCode) ? (
    <BorderManualMeasurementsStep
      borderData={borderData}
      needsOrientation={needsOrientation}
      isCornerOrientationRequired={isCornerOrientationRequired}
      canEdit={canEdit}
      isAdmin={isAdmin}
      isLoading={disabled}
      next={next}
      back={back}
      onSubmit={saveManualMeasurements}
    />
  ) : (
    <BorderAggregateMeasurementsStep
      borderData={borderData}
      isPerimeterSplitRequired={isPerimeterSplitRequired}
      isCornerOrientationRequired={isCornerOrientationRequired}
      canEdit={canEdit}
      isAdmin={isAdmin}
      isLoading={disabled}
      next={next}
      back={back}
      onSubmit={saveAggregateMeasurements}
    />
  );
};

export default BorderPerimeterStep;
